import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../../lib/api'
import { ClickToCopy } from '../ClickToCopy'
import { getDashboardAppHostPath } from '../../lib/appSlug'
import type { AppRecord } from '../../../shared/types/apps'

type AppFormState = {
  name: string
  slug: string
  domain: string
  is_private: boolean
}

const INITIAL_FORM: AppFormState = {
  name: '',
  slug: '',
  domain: '',
  is_private: false,
}

function normalizeApp(row: any): AppRecord {
  return {
    app_id: Number(row?.app_id ?? 0),
    name: row?.name ?? '',
    slug: row?.slug ?? '',
    domain: row?.domain ?? null,
    user_id: Number(row?.user_id ?? 0),
    private: Number(row?.private ?? 0),
    created_at: Number(row?.created_at ?? Date.now()),
    updated_at: Number(row?.updated_at ?? Date.now()),
  }
}

function slugifyName(value: string): string {
  const trimmed = value.trim().toLowerCase()
  if (!trimmed) {
    return ''
  }
  return trimmed
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function formatDomain(value: string): string {
  return value.trim()
}

export function AppsPanel() {
  const [apps, setApps] = useState<AppRecord[]>([])
  const [selectedAppId, setSelectedAppId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formState, setFormState] = useState<AppFormState>(INITIAL_FORM)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const selectedApp = useMemo(() => {
    if (selectedAppId === null) {
      return null
    }
    return apps.find((app) => app.app_id === selectedAppId) ?? null
  }, [apps, selectedAppId])

  const refreshApps = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false
    if (!silent) {
      setLoading(true)
      setError(null)
    }
    try {
      const list = await api().apps.list()
      if (!mountedRef.current) {
        return
      }
      const normalized = Array.isArray(list) ? list.map(normalizeApp) : []
      setApps(normalized)
    } catch (err) {
      console.error('[AppsPanel] Failed to load apps:', err)
      if (mountedRef.current && !silent) {
        setError('Unable to load apps')
        setApps([])
      }
    } finally {
      if (mountedRef.current && !silent) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    void refreshApps()
  }, [refreshApps])

  useEffect(() => {
    if (!selectedAppId) {
      setFormState(INITIAL_FORM)
      return
    }
    const app = apps.find((entry) => entry.app_id === selectedAppId)
    if (!app) {
      setSelectedAppId(null)
      setFormState(INITIAL_FORM)
      return
    }
    setFormState({
      name: app.name ?? '',
      slug: app.slug ?? '',
      domain: app.domain ?? '',
      is_private: Number(app.private) === 1,
    })
  }, [apps, selectedAppId])

  const handleSelectApp = (appId: number) => {
    setSelectedAppId(appId)
    setError(null)
  }

  const handleNewApp = () => {
    setSelectedAppId(null)
    setFormState(INITIAL_FORM)
    setError(null)
  }

  const handleNameChange = (value: string) => {
    setFormState((prev) => {
      const derived = slugifyName(value)
      const prevDerived = slugifyName(prev.name)
      const shouldAuto = !prev.slug.trim() || prev.slug.trim() === prevDerived
      return {
        ...prev,
        name: value,
        slug: shouldAuto ? derived : prev.slug,
      }
    })
  }

  const handleFieldChange = <K extends keyof AppFormState>(field: K, value: AppFormState[K]) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleCreate = async () => {
    const name = formState.name.trim()
    const slug = formState.slug.trim() || slugifyName(name)
    if (!name) {
      setError('App name is required')
      return
    }
    if (!slug) {
      setError('App slug is required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const created = await api().apps.create({
        name,
        slug,
        domain: formatDomain(formState.domain) || null,
        private: formState.is_private,
      })
      if (!mountedRef.current) {
        return
      }
      const normalized = normalizeApp(created)
      await refreshApps({ silent: true })
      setSelectedAppId(normalized.app_id)
    } catch (err) {
      console.error('[AppsPanel] Failed to create app:', err)
      if (mountedRef.current) {
        setError('Failed to create app')
      }
    } finally {
      if (mountedRef.current) {
        setSaving(false)
      }
    }
  }

  const handleUpdate = async () => {
    if (!selectedApp) {
      return
    }
    const name = formState.name.trim()
    const slug = formState.slug.trim()
    if (!name) {
      setError('App name is required')
      return
    }
    if (!slug) {
      setError('App slug is required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await api().apps.update(selectedApp.app_id, {
        name,
        slug,
        domain: formatDomain(formState.domain) || null,
        private: formState.is_private,
      })
      if (!mountedRef.current) {
        return
      }
      await refreshApps({ silent: true })
    } catch (err) {
      console.error('[AppsPanel] Failed to update app:', err)
      if (mountedRef.current) {
        setError('Failed to update app')
      }
    } finally {
      if (mountedRef.current) {
        setSaving(false)
      }
    }
  }

  const handleDelete = async () => {
    if (!selectedApp) {
      return
    }
    setDeleting(true)
    setError(null)
    try {
      await api().apps.delete(selectedApp.app_id)
      if (!mountedRef.current) {
        return
      }
      await refreshApps({ silent: true })
      setSelectedAppId(null)
      setFormState(INITIAL_FORM)
    } catch (err) {
      console.error('[AppsPanel] Failed to delete app:', err)
      if (mountedRef.current) {
        setError('Failed to delete app')
      }
    } finally {
      if (mountedRef.current) {
        setDeleting(false)
      }
    }
  }

  if (loading) {
    return (
      <div className="panel-shell">
        <div className="panel-content panel-content--horizontal">
          <div className="empty-state">
            <p>Loading apps...</p>
          </div>
        </div>
      </div>
    )
  }

  const slug = formState.slug.trim()
  const appUrl = slug ? getDashboardAppHostPath(slug) : ''
  const dashboardUrl = slug ? `/dashboard/${slug}` : ''

  const handleVisit = () => {
    if (!appUrl) {
      return
    }
    window.open(appUrl, '_blank', 'noopener')
  }

  const handleDashboard = () => {
    if (!dashboardUrl) {
      return
    }
    window.location.assign(dashboardUrl)
  }

  return (
    <div className="panel-shell">
      <div className="panel-controls">
        <button type="button" className="btn" onClick={handleNewApp}>
          New app
        </button>
        {error && <span className="panel-error">{error}</span>}
      </div>
      <div className="panel-content panel-content--horizontal apps-manager">
        <div className="apps-manager__column apps-manager__column--editor">
          <div className="app-editor__wrapper">
            <form
              className="app-editor"
              onSubmit={(event) => {
                event.preventDefault()
              }}
            >
              <div className="app-editor__row">
                <div className="app-editor__section">
                  <label className="app-editor__label" htmlFor="app-name">
                    Name
                  </label>
                  <input
                    id="app-name"
                    type="text"
                    className="app-editor__input"
                    value={formState.name}
                    onChange={(event) => handleNameChange(event.target.value)}
                    placeholder="My app"
                    autoComplete="off"
                  />
                </div>
                <div className="app-editor__section">
                  <label className="app-editor__label">App ID</label>
                  {selectedApp ? (
                    <ClickToCopy
                      value={String(selectedApp.app_id)}
                      className="app-editor__copy"
                      title="Click to copy app ID"
                      ariaLabel="Copy app ID to clipboard"
                    />
                  ) : (
                    <div className="app-editor__placeholder">—</div>
                  )}
                </div>
              </div>

              <div className="app-editor__row">
                <div className="app-editor__section">
                  <label className="app-editor__label" htmlFor="app-slug">
                    Slug
                  </label>
                  <input
                    id="app-slug"
                    type="text"
                    className="app-editor__input"
                    value={formState.slug}
                    onChange={(event) => handleFieldChange('slug', event.target.value)}
                    placeholder="my-app"
                    autoComplete="off"
                  />
                  <p className="app-editor__hint">Lowercase letters, numbers, and hyphens only</p>
                </div>
                <div className="app-editor__section">
                  <label className="app-editor__label" htmlFor="app-domain">
                    Domain
                  </label>
                  <input
                    id="app-domain"
                    type="text"
                    className="app-editor__input"
                    value={formState.domain}
                    onChange={(event) => handleFieldChange('domain', event.target.value)}
                    placeholder="app.example.com"
                    autoComplete="off"
                  />
                </div>
              </div>

              <div className="app-editor__row">
                <div className="app-editor__section">
                  <label className="app-editor__label">Visibility</label>
                  <div className="app-editor__toggle">
                    <input
                      type="checkbox"
                      className="input-checkbox"
                      checked={formState.is_private}
                      onChange={(event) => handleFieldChange('is_private', event.target.checked)}
                    />
                    <span className="app-editor__toggle-label">
                      {formState.is_private ? 'Private' : 'Public'}
                    </span>
                  </div>
                  <p className="app-editor__hint">Private apps require login to view</p>
                </div>
                <div className="app-editor__section">
                  <label className="app-editor__label">App URL</label>
                  {appUrl ? (
                    <ClickToCopy
                      value={appUrl}
                      className="app-editor__copy"
                      title="Click to copy app URL"
                      ariaLabel="Copy app URL to clipboard"
                    />
                  ) : (
                    <div className="app-editor__placeholder">—</div>
                  )}
                </div>
              </div>
            </form>
          </div>
          <div className="app-editor__footer">
            <div className="app-editor__button-group">
              <button
                type="button"
                className="btn"
                onClick={handleVisit}
                disabled={!appUrl}
              >
                Visit
              </button>
              <button
                type="button"
                className="btn"
                onClick={handleDashboard}
                disabled={!dashboardUrl}
              >
                Dashboard
              </button>
              {selectedApp && (
                <button
                  type="button"
                  className="btn btn--delete"
                  onClick={() => {
                    void handleDelete()
                  }}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              )}
              <button
                type="button"
                className="btn"
                onClick={() => {
                  void (selectedApp ? handleUpdate() : handleCreate())
                }}
                disabled={saving}
              >
                {saving ? 'Saving...' : selectedApp ? 'Save changes' : 'Create app'}
              </button>
            </div>
          </div>
        </div>
        <div className="apps-manager__column apps-manager__column--list">
          {apps.length ? (
            <ul className="apps-list">
              {apps.map((app) => {
                const isSelected = app.app_id === selectedAppId
                const isPrivate = Number(app.private) === 1
                return (
                  <li key={app.app_id}>
                    <button
                      type="button"
                      className={`apps-list__row${isSelected ? ' apps-list__row--selected' : ''}`}
                      onClick={() => handleSelectApp(app.app_id)}
                    >
                      <div className="apps-list__row-header">
                        <div className="apps-list__name-section">
                          <span className="apps-list__name">{app.name || 'Untitled app'}</span>
                          <span className="apps-list__slug">{app.slug}</span>
                        </div>
                        <div className="apps-list__badges">
                          <span className={`badge ${isPrivate ? 'badge--neutral' : 'badge--positive'}`}>
                            {isPrivate ? 'private' : 'public'}
                          </span>
                        </div>
                      </div>
                      <div className="apps-list__row-footer">
                        {app.domain ? (
                          <span className="apps-list__domain">{app.domain}</span>
                        ) : (
                          <span className="apps-list__domain apps-list__domain--empty">No domain</span>
                        )}
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          ) : (
            <div className="apps-list__empty">
              <div className="empty-state">
                <p>No apps yet</p>
                <p className="empty-state-subtitle">Create an app to start hosting builds</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
